import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import { Attachment } from '@ee/common/models';
import { DocumentService } from '@ee/common/services';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { PdfPreviewDialogComponent } from '@ee/common/pdf-preview-dialog';

@Component({
  selector: 'ee-intake-doc-file',
  template: `
    <div class="file">
      <div class="name">
        <div class="icon">
          <i class="fa-solid" [ngClass]="generateIconName(attachment)"></i>
        </div>
        <div class="type">{{ attachment.type }}</div>
      </div>
      @if (hasMetadata) {
        <div class="metadata hidden lg:flex">
          @for (field of attachment.extracted_fields | keyvalue; track field.key) {
            @if (field.key === 'tenant_names') {
              <div class="metadata-item" tp="Tenant Names">
                <i class="fa-regular fa-users"></i>
                {{ field.value }}
              </div>
            } @else if (field.key === 'lease_end_date') {
              <div class="metadata-item" tp="Lease End Date">
                <i class="fa-regular fa-calendar-xmark"></i>
                {{ field.value }}
              </div>
            } @else if (field.key === 'lease_start_date') {
              <div class="metadata-item" tp="Lease Start Date">
                <i class="fa-regular fa-calendar-check"></i>
                {{ field.value }}
              </div>
            } @else if (field.key === 'owner_names') {
              <div class="metadata-item" tp="Owner Names">
                <i class="fa-regular fa-users"></i>
                {{ field.value }}
              </div>
            } @else if (field.key === 'lease_property_address') {
              <div class="metadata-item" tp="Property Address">
                <i class="fa-regular fa-users"></i>
                {{ field.value }}
              </div>
            } @else {
              <div class="metadata-item" tp="{{ field.key }}">
                <i class="fa-regular code"></i>
                {{ field.value }}
              </div>
            }
          }
        </div>
      }
      <div class="view-options">
        @if (hasMetadata) {
          <ee-intake-doc-metadata-info class="option block ml-2" [attachment]="attachment" />
        }
        @if (attachment.mime_type === 'application/pdf') {
          <i class="option fa-regular fa-eye ml-2" tp="Preview"
          (click)="previewDocument(attachment)"></i>
        }
        <i class="option fa-regular fa-download ml-2" tp="Download" (click)="downloadDocument(attachment)"></i>
        @if (removable) {
          <i class="option fa-regular fa-trash ml-2" tp="Remove" (click)="remove.emit()"></i>
        }
      </div>
    </div>
    `,
  styles: [
    `

      .file {
        -webkit-user-select: none;
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        text-align: center;
        padding: 3px 10px;
        background-color: rgba(213, 0, 0, 0.05);
        border-radius: 3px;

        .name {
          display: flex;
          flex-direction: row;
          line-height: 30px;

          .icon {
            color: var(--primary-red);
            opacity: 0.5;
            margin-right: 10px;
          }

          .type {
            white-space: nowrap;
            text-overflow: ellipsis;
            display: inline-block;
            overflow: hidden;
            text-align: left;
            width: 100%;
            font-size: 14px;
            color: var(--color-on-surface, inherit);
          }
        }

        .metadata {
          flex: 1;
          flex-direction: row;
          align-items: center;
          justify-content: start;
          margin: 0 1rem;
          overflow-x: auto;
          overflow-y: hidden;
          scrollbar-width: none;

          .metadata-item {
            color: var(--color-on-surface-variant, #6f6f6f);
            display: flex;
            flex-direction: row;
            align-items: center;
            white-space: nowrap;
            margin-right: 10px;
            padding: 0 4px;

            i {
              color: var(--color-on-surface-variant, #6f6f6f);
              margin-right: 4px;
            }
          }
        }

        .metadata-item,
        .option {
          font-size: 12px;
          background-color: var(--color-surface, #fff);
          height: 20px;
        }

        .view-options {
          margin: auto 0;
          display: flex;
          flex-direction: row;

          .metadata {
            display: flex;
            flex-direction: row;
            align-items: center;
            justify-content: start;
          }

          .option {
            border-radius: 10px;
            line-height: 20px;
            width: 20px;
            cursor: pointer;

            &.fa-download {
              color: var(--color-on-surface-variant, #6f6f6f);
            }

            &.fa-eye {
              color: var(--primary-red);
            }

            &:hover {
              background-color: var(--color-surface-container-highest, #d7d7d7);
            }
          }
        }
      }
    `
  ],
  standalone: false
})
export class IntakeDocFileComponent implements OnInit {

  @Input({ required: true }) attachment: Attachment;

  @Input({ required: false }) intakeEvictionId: string | null = null;

  @Input({ required: false }) removable: boolean = false;

  @Input({ required: false }) caseId: string | null = null;

  @Output() remove = new EventEmitter<void>();

  public hasMetadata = false;

  private readonly unsafeFileMessage = 'This file has been found to be unsafe and is not available for download.';

  constructor(private documentService: DocumentService, private snackBar: MatSnackBar, private dialog: MatDialog) {}

  ngOnInit() {
    this.hasMetadata = !!this.attachment.extracted_fields && Object.keys(this.attachment.extracted_fields).length > 0;
  }

  generateIconName(attachment: Attachment) {
    let icon = 'fa-file';
    if (!!attachment.virus_scan_date && !attachment.clean) {
      icon = 'fa-file-circle-exclamation';
    } else if (attachment.mime_type) {
      if (attachment.mime_type === 'application/pdf') {
        icon = 'fa-file-pdf';
      } else if (attachment.mime_type.indexOf('wordprocessingml') > -1) {
        icon = 'fa-file-word';
      } else if (attachment.mime_type.indexOf('spreadsheetml') > -1) {
        icon = 'fa-file-excel';
      }
    }
    return icon;
  }

  async downloadDocument(attachment: Attachment) {
    if (this.caseId) {
      await this.documentService.loadAndSaveAttachmentByCase(this.caseId, attachment);
    } else {
      if (typeof attachment.clean === 'boolean' && !attachment.clean) {
        this.snackBar.open(this.unsafeFileMessage, 'Dismiss', {
          duration: 3000
        });
      } else {
        try {
          await this.documentService.loadAndSaveIntakeDoc(this.intakeEvictionId, attachment);
        } catch (e) {
          if (e.status === 404) {
            this.snackBar.open('File cannot be found. Please contact EasyEviction for help.', 'Dismiss', {
              duration: 3000
            });
          }
        }
      }
    }
  }

  previewDocument(attachment: Attachment) {
    if (this.caseId && this.attachment?.id) {
      this.documentService.loadDocumentByCase(this.caseId, this.attachment.id, true).subscribe((downloadUrl: string) => {
        this.dialog.open(PdfPreviewDialogComponent, {
          width: '75%',
          data: {
            attachment,
            downloadUrl
          }
        });
      });
    } else {
      this.documentService
        .loadIntakeDocById(this.intakeEvictionId, this.attachment.id, true)
        .subscribe((downloadUrl: string) => {
          this.dialog.open(PdfPreviewDialogComponent, {
            width: '75%',
            data: {
              attachment,
              downloadUrl
            }
          });
        });
    }
  }
}

